/**
 * Tous les mots que le plateau montre (C28, spec § 9) : plateau, carte, îlot, tiroir, rail,
 * mot, concept, passage, note, réponse — jamais un mot de code. Criblé par
 * `metanoteLabels.test.ts`. Les composants ne contiennent aucune chaîne d'écran.
 */
const pluriel = (n: number, un: string, des: string) => `${n} ${n > 1 ? des : un}`;

export const METANOTE_LABELS = Object.freeze({
  section: 'Méta Notes',
  nouveau: 'Nouveau plateau',
  retourListe: 'Revenir à la liste',
  titreParDefaut: 'Sans titre',
  annulerAction: 'Annuler',
  liste: Object.freeze({
    titre: 'Vos plateaux',
    vide: Object.freeze({ titre: 'Aucun plateau pour l\'instant', phrase: 'Un plateau part de quelques mots et finit en note.' }),
    compte: (cartes: number, ilots: number) => `${pluriel(cartes, 'carte', 'cartes')} · ${pluriel(ilots, 'îlot', 'îlots')}`,
    noteEcrite: (date: string) => `Note écrite le ${date}`,
    sansNote: 'Pas encore de note',
    ouvrir: 'Ouvrir',
    supprimer: 'Supprimer',
    confirmerSuppression: (titre: string) => `Supprimer le plateau « ${titre} » ? Les notes écrites depuis ce plateau restent.`,
    chargement: 'Chargement de vos plateaux…',
    erreur: 'Impossible de lire vos plateaux. Réessayez.',
  }),
  enregistrement: Object.freeze({
    enregistre: 'Enregistré',
    enAttente: 'Non enregistré',
    enCours: 'Enregistrement…',
    conflit: 'Ce plateau a été modifié ailleurs. Recharger.',
    /** Revue après merge du 19/10 : une réponse posée pendant le conflit n'est pas perdue au rechargement. */
    conflitReponses: 'Les réponses de l\'assistant posées ici et absentes du plateau rechargé seront reposées dans leur cadre.',
    echec: 'Enregistrement impossible. Réessayez.',
    recharger: 'Recharger',
    reessayer: 'Réessayer',
    quitterSansEnregistrer: 'Des modifications ne sont pas enregistrées. Quitter quand même ?',
  }),
  outils: Object.freeze({
    selectionner: 'Sélectionner',
    selectionnerAide: 'Sélectionner ce qu\'un rectangle contient (S, puis glisser ; Maj ajoute à la sélection)',
    /** C28 SP4 : la carte « Texte » (sorte `mot` dans le code) — un mot se rapproche, un texte montre ses concepts reconnus. */
    mot: 'Texte',
    motAide: 'Écrire un mot ou du texte (double-clic sur le vide, ou N)',
    ilot: 'Îlot',
    ilotAide: 'Tracer un cadre (I, puis glisser sur le vide)',
    /** C28 SP4 : l'outil « Flèche ». */
    fleche: 'Flèche',
    flecheAide: 'Tracer une flèche (F, puis glisser d\'un point à un autre)',
    tiroir: 'Tiroir',
    tiroirAide: 'Ouvrir le tiroir (un concept ou une note sélectionnés)',
    recentrer: 'Recentrer',
    annuler: 'Annuler le dernier geste (Ctrl + Z)',
    ecrireLaNote: 'Écrire la note',
    completerLaNote: 'Compléter la note',
    ouvrirLaNote: 'Ouvrir la note',
    plafond: (cartes: number, ilots: number) => `${pluriel(cartes, 'carte', 'cartes')} et ${pluriel(ilots, 'îlot', 'îlots')} : le plateau devient lourd, ouvrez-en un second.`,
  }),
  carte: Object.freeze({
    motPlaceholder: 'Un mot, un concept, une idée…',
    rapprocher: 'Rapprocher de',
    /** C28 SP4 : les concepts reconnus dans un texte, en pastilles informatives. */
    reconnus: 'Reconnus :',
    nouveauConcept: 'Nouveau concept…',
    poseComme: (mot: string) => `Posé comme « ${mot} »`,
    passageDisparu: 'Ce passage n\'est plus dans la note.',
    source: (titre: string) => `Source : ${titre}`,
    page: (page: string) => `p. ${page}`,
    chapitre: (chapitre: string) => `chap. ${chapitre}`,
    reponse: 'Réponse de l\'assistant',
    ouvrir: 'Ouvrir',
    /** C23 SP9 : une carte d'une autre personne — la pastille sous la sorte, et le corps quand sa porte s'est refermée. */
    de: (prenom: string) => `de ${prenom || 'une autre personne'}`,
    indisponible: 'Indisponible : cette note n\'est plus parmi celles que vous pouvez lire.',
    retirer: 'Retirer du plateau',
    copier: 'Copier (Alt + glisser)',
    sorte: Object.freeze({ mot: 'Texte', concept: 'Concept', passage: 'Passage', note: 'Note', reponse: 'Réponse' }),
  }),
  /** C28 SP4 : une flèche de lecture entre deux endroits du plateau (jamais « lien », qui est la note reliée, ni « connecteur »). */
  fleche: Object.freeze({
    sorte: 'Flèche',
    de: (de: string, vers: string) => `Flèche de ${de} vers ${vers}`,
    point: 'un point du plateau',
    cadre: (nom: string) => `cadre « ${nom || 'partie sans nom'} »`,
    carte: (sorte: string, texte: string) => `${sorte} « ${texte} »`,
    etiquette: 'Étiquette',
    etiquettePlaceholder: 'Un mot ou deux…',
    retirer: 'Retirer la flèche',
  }),
  /** C28 SP4 : la remarque de la personne sur une carte (jamais « commentaire », qui est ce qu'autrui écrit sous une note). */
  remarque: Object.freeze({
    titre: 'Remarque',
    ajouter: 'Ajouter une remarque',
    placeholder: 'Ce que vous en pensez…',
    voirTout: 'Voir tout',
    replier: 'Replier',
  }),
  ilot: Object.freeze({
    nomPlaceholder: 'Nom de la partie',
    phrasePlaceholder: 'Une phrase de travail pour cette partie…',
    phrase: 'Phrase de travail',
    supprimer: 'Retirer le cadre (les cartes restent)',
    demander: 'Demander',
    demanderAide: 'Demander à l\'assistant sur les notes et les concepts de ce cadre',
    demanderVide: 'Posez d\'abord un passage, une note, un concept ou un texte dans ce cadre.',
    compte: (n: number) => pluriel(n, 'carte', 'cartes'),
    /** C28 SP3 bis : le menu de l'assistant sur un cadre. */
    assistant: 'Assistant',
    demanderMenu: 'Demander…',
    sansNom: 'partie sans nom',
    gesteSansTitre: 'Nommez d\'abord cette partie.',
    envoiEnCours: 'L\'assistant répond déjà : attendez la réponse.',
    enregistrementEnCours: 'Enregistrement du plateau en cours…',
  }),
  /** Les gestes fixes sur un cadre (C28 SP3 bis) : la consigne vit au serveur, seul le nom est ici. */
  gestes: Object.freeze({
    commun: 'Ce qu\'elles ont en commun',
    divergence: 'Où elles divergent',
    manque: 'Ce qui manque pour tenir ce titre',
    titres: 'Titres possibles',
  }),
  /** La fenêtre de lecture d'une carte « Réponse » (C28 SP3 bis). */
  reponse: Object.freeze({
    ouvrir: 'Lire la réponse',
    titre: 'Réponse de l\'assistant',
    poseeLe: (date: string) => `Posée le ${date}`,
    sources: 'Sources',
    ouvrirNote: (titre: string) => `Ouvrir la note « ${titre} »`,
    voirPassage: (titre: string) => `Voir le passage dans « ${titre} »`,
    aucuneSource: 'Aucune source citée.',
  }),
  rail: Object.freeze({
    titre: 'Ordre de la note',
    vide: 'Tracez un premier cadre : chaque cadre devient une partie de la note.',
    horsIlot: (n: number) => (n > 0 ? `${pluriel(n, 'carte', 'cartes')} hors îlot` : ''),
    monter: 'Avancer (Alt + ←)',
    descendre: 'Reculer (Alt + →)',
  }),
  tiroir: Object.freeze({
    titre: 'Tiroir',
    recherche: 'Rechercher dans vos notes…',
    rechercher: 'Rechercher',
    passages: 'Passages',
    notesReliees: 'Notes reliées',
    autresNotes: 'Autres notes qui portent ce concept',
    resultats: 'Résultats',
    aucunConcept: 'Sélectionnez un concept ou une note sur le plateau pour voir ses passages.',
    aucunPassage: 'Aucun passage validé pour ce concept.',
    /** 25/10/2026 : le tiroir d'une note sélectionnée. */
    aucunPassageNote: 'Aucun passage validé dans cette note.',
    noteAbsente: 'Cette note n\'est plus dans vos notes.',
    noteDAutrui: 'Cette note n\'est pas la vôtre : ses passages se tirent de ses concepts, pas d\'ici.',
    poserLeConcept: 'Poser le concept',
    conceptDejaPose: 'Concept sur le plateau',
    /** C23 SP9 : l'entrée d'un concept d'autrui, depuis le tiroir de l'un des miens (la découverte de SP6/SP7). */
    faceAFaceAvec: 'Face à face avec…',
    faceAFaceAvecVide: 'Personne, pour l\'instant, parmi les notes que vous pouvez lire.',
    faceAFaceAvecErreur: 'Impossible de lire « Face à face avec… ».',
    sansNom: 'Un lecteur',
    /** C23 SP9 : le tiroir d'un concept d'autrui, nourri par le face à face. */
    passagesDe: (prenom: string) => `Les passages de ${prenom || 'cette personne'} viennent de ses notes que vous pouvez lire. « Écrire la note » les citera avec leur source, sans reposer ses concepts dans la vôtre.`,
    aucunPassageDe: (prenom: string) => `Aucun passage de ${prenom || 'cette personne'} pour l’instant.`,
    faceAFaceIndisponible: 'Face à face indisponible.',
    faceAFaceErreur: 'Impossible de lire ce face à face.',
    lecture: 'Lecture…',
    reessayer: 'Réessayer',
    tronque: 'Tout n\'est pas montré : les premiers passages seulement.',
    aucunResultat: 'Rien ne correspond.',
    rechercheEnCours: 'Recherche…',
    rechercheEchec: 'La recherche a échoué. Réessayez.',
    poser: 'Poser',
    /** 24/10/2026 : sur chaque passage, le passage seul ou la note entière. */
    poserLePassage: 'Poser le passage',
    poserLaNote: 'Poser la note',
    noteDejaPosee: 'Note sur le plateau',
    dejaPose: 'Sur le plateau',
    /** Une note écrite depuis un plateau (« Écrire la note », SP2). */
    ecriteDepuis: (plateau: string) => `Écrite depuis « ${plateau} »`,
    ecriteIci: 'Écrite depuis ce plateau',
    piste: 'Piste',
    relation: Object.freeze({ soutient: 'soutient', oppose: 's\'oppose', suite: 'fait suite' }),
    fermer: 'Fermer le tiroir',
  }),
  /** « Écrire la note » (C28 SP2, spec § 5.1). */
  ecrire: Object.freeze({
    titre: 'Écrire la note',
    titreChamp: 'Titre de la note',
    dossier: 'Dossier',
    resume: (sections: number, citations: number, horsIlot: number, indisponibles: number = 0) => {
      const parts = [pluriel(sections, 'section', 'sections'), citations > 0 ? pluriel(citations, 'citation', 'citations') : 'aucune citation'];
      if (horsIlot > 0) parts.push(`${pluriel(horsIlot, 'carte', 'cartes')} hors îlot ${horsIlot > 1 ? 'ne seront pas écrites' : 'ne sera pas écrite'}`);
      // C23 SP9 : une carte d'autrui dont la porte s'est refermée n'est pas écrite.
      if (indisponibles > 0) parts.push(`${pluriel(indisponibles, 'passage indisponible', 'passages indisponibles')} ${indisponibles > 1 ? 'ne seront pas écrits' : 'ne sera pas écrit'}`);
      return parts.join(' · ');
    },
    ordre: 'Dans chaque îlot, les cartes s\'écrivent de haut en bas puis de gauche à droite.',
    aucunIlot: 'Tracez un premier cadre : chaque cadre devient une partie de la note.',
    enAttente: 'Enregistrement du plateau en cours…',
    ecrire: 'Écrire',
    annuler: 'Annuler',
    enCours: 'Écriture de la note…',
    echecNote: 'La note n\'a pas pu être écrite. Réessayez.',
    echecPlateau: 'La note est écrite, mais le plateau n\'a pas pu s\'en souvenir. Réessayez.',
    reessayer: 'Réessayer',
  }),
  /** « Compléter la note » (C28 SP2, spec § 5.3). */
  completer: Object.freeze({
    titre: 'Compléter la note',
    rien: 'Rien de nouveau à écrire : toutes les cartes des îlots sont déjà dans la note.',
    enSection: (titre: string) => `en fin de la section « ${titre} »`,
    enFin: 'en fin de la note',
    nouvelIlot: 'nouvelle partie, en fin de la note',
    introuvable: 'section introuvable (titre renommé ou retiré) : en fin de la note',
    homonymes: 'Deux parties portent ce nom : la première reçoit.',
    phraseNonReportee: 'Une phrase de travail modifiée n\'est pas reportée : elle appartient à la note depuis l\'écriture.',
    completer: 'Compléter',
    annuler: 'Annuler',
    relecture: 'Relecture de la note…',
    disparue: 'La note écrite depuis ce plateau n\'existe plus.',
    echec: 'La note n\'a pas pu être complétée. Réessayez.',
    echecPlateau: 'La note est complétée, mais le plateau n\'a pas pu s\'en souvenir. Réessayez.',
    reessayer: 'Réessayer',
    cartesCochees: (n: number) => `${pluriel(n, 'carte cochée', 'cartes cochées')}`,
  }),
  /** La note écrite depuis le plateau (C28 SP2, spec § 7). */
  noteDuPlateau: Object.freeze({
    ouvrir: 'Ouvrir la note',
    disparue: 'La note écrite depuis ce plateau n\'existe plus.',
    ecrireUneAutre: 'Écrire une nouvelle note',
  }),
  /** « Revenir au plateau », au pied de la note écrite (C28 SP2, spec § 5.2). */
  revenir: Object.freeze({
    ecriteDepuis: (titre: string) => `Écrite depuis le plateau « ${titre} »`,
    bouton: 'Revenir au plateau',
  }),
  aide: Object.freeze({
    titre: 'Méta Notes',
  }),
});

export type MetaNoteLabels = typeof METANOTE_LABELS;
